import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { supabase } from '../../../lib/supabase';
import { useAppContext } from '../../../contexts/AppContext';
import { generatePartnerCodeFromCompany } from '../../../domain/partners/commission';
import {
  formatCnpjDisplay,
  validatePartnerApplication,
} from '../../../domain/partners/application';
import { notify } from '../../../utils/toast';
import { usePartnerProfile } from './usePartnerDashboard';

export function usePartnerApply() {
  const { setCurrentPage } = useAppContext();
  const { partner, isLoading, refetch } = usePartnerProfile();

  const [companyName, setCompanyName] = useState('');
  const [cnpj, setCnpj] = useState('');
  const [fieldErrors, setFieldErrors] = useState<{
    companyName?: string;
    cnpj?: string;
  }>({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isLoading && partner) {
      setCurrentPage('parceiro');
    }
  }, [isLoading, partner, setCurrentPage]);

  const onCompanyNameChange = useCallback((value: string) => {
    setCompanyName(value);
    setFieldErrors((prev) => ({ ...prev, companyName: undefined }));
  }, []);

  const onCnpjChange = useCallback((value: string) => {
    setCnpj(formatCnpjDisplay(value));
    setFieldErrors((prev) => ({ ...prev, cnpj: undefined }));
  }, []);

  const submit = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();

      const validation = validatePartnerApplication({ companyName, cnpj });
      if (validation.ok === false) {
        setFieldErrors({ [validation.field]: validation.message });
        notify.error(validation.message);
        return;
      }

      setSubmitting(true);
      setFieldErrors({});

      try {
        // Prefer getUser() over getSession() — validates JWT with Auth server.
        const {
          data: { user },
          error: authError,
        } = await supabase.auth.getUser();

        if (authError || !user) {
          notify.error('Sessão expirada. Faça login novamente.');
          return;
        }

        const code = generatePartnerCodeFromCompany(validation.payload.company_name);

        const { error } = await supabase.from('partners').insert({
          user_id: user.id,
          code,
          company_name: validation.payload.company_name,
          cnpj: validation.payload.cnpj,
          status: validation.payload.status,
          commission_rate: validation.payload.commission_rate,
        });

        if (error) {
          if (error.code === '23505') {
            notify.error('Você já possui uma candidatura de parceiro.');
            await refetch();
            return;
          }
          throw error;
        }

        notify.success('Candidatura enviada! Aguarde a aprovação.');
        await refetch();
        setCurrentPage('parceiro');
      } catch (err) {
        notify.error(err instanceof Error ? err.message : 'Erro ao enviar candidatura');
      } finally {
        setSubmitting(false);
      }
    },
    [companyName, cnpj, refetch, setCurrentPage],
  );

  return {
    partner,
    isLoading,
    companyName,
    cnpj,
    fieldErrors,
    submitting,
    onCompanyNameChange,
    onCnpjChange,
    submit,
  };
}
